import { useEffect, useState } from "react";
import { toast } from "sonner";
import { apiCreate, apiDelete, apiList, apiUpdate } from "@/lib/api";
import { useActiveClinicaId } from "@/hooks/useActiveClinicaId";
import { parseMoneyBR, roundMoney } from "@/lib/money";
import { useAuth } from "@/contexts/AuthContext";
import { normalizeLocalDate } from "@/lib/date-utils";

export type StatusPagamentoFuncionario = "Pendente" | "Pago" | "Cancelado";

export interface PagamentoFuncionario {
  id: string;
  user_id?: string;
  clinica_id?: string;
  clinica_dados?: Record<string, unknown>;
  funcionario_id: string;
  funcionario_nome: string;
  cargo?: string;
  competencia_inicio: string;
  competencia_fim: string;
  salario_base: number;
  producao: number;
  percentual_comissao: number;
  valor_comissao: number;
  bonus_meta: number;
  descontos: number;
  valor_total: number;
  forma_pagamento?: string;
  data_pagamento?: string;
  status: StatusPagamentoFuncionario;
  origem_calculo?: "automatico" | "manual";
  base_comissao?: "unidade" | "producao_pessoal";
  producao_itens?: Array<{ id: string; origem: "agendamento" | "ordem_servico"; data: string; descricao: string; valor: number }>;
  observacoes?: string;
  created_at?: string;
  updated_at?: string;
}

export type PagamentoFuncionarioInput = Omit<PagamentoFuncionario, "id" | "created_at" | "updated_at">;

const normalizePagamentoFuncionario = <T extends Partial<PagamentoFuncionario>>(data: T): T => ({
  ...data,
  ...(data.competencia_inicio ? { competencia_inicio: normalizeLocalDate(data.competencia_inicio) } : {}),
  ...(data.competencia_fim ? { competencia_fim: normalizeLocalDate(data.competencia_fim) } : {}),
  ...("data_pagamento" in data ? { data_pagamento: normalizeLocalDate(data.data_pagamento) } : {}),
});

export function calcularPagamentoFuncionario(input: {
  salario_base?: number;
  producao?: number;
  percentual_comissao?: number;
  bonus_meta?: number;
  descontos?: number;
}) {
  const salarioBase = parseMoneyBR(input.salario_base);
  const producao = parseMoneyBR(input.producao);
  const percentualComissao = Number(input.percentual_comissao || 0);
  const valorComissao = roundMoney(producao * percentualComissao / 100);
  const bonusMeta = parseMoneyBR(input.bonus_meta);
  const descontos = parseMoneyBR(input.descontos);
  const valorTotal = roundMoney(Math.max(0, salarioBase + valorComissao + bonusMeta - descontos));

  return {
    salario_base: salarioBase,
    producao,
    percentual_comissao: percentualComissao,
    valor_comissao: valorComissao,
    bonus_meta: bonusMeta,
    descontos,
    valor_total: valorTotal,
  };
}

export function usePagamentosFuncionarios() {
  const { user } = useAuth();
  const activeClinicaId = useActiveClinicaId();
  const [pagamentos, setPagamentos] = useState<PagamentoFuncionario[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchPagamentos = async () => {
    if (!user) {
      setPagamentos([]);
      return;
    }
    try {
      setLoading(true);
      const data = await apiList<PagamentoFuncionario>("pagamentos_funcionarios");
      setPagamentos((data || []).map(normalizePagamentoFuncionario).sort((a, b) => String(b.competencia_fim || "").localeCompare(String(a.competencia_fim || ""))));
    } catch (error: any) {
      toast.error("Erro ao carregar pagamentos de funcionários: " + error.message);
      setPagamentos([]);
    } finally {
      setLoading(false);
    }
  };

  const createPagamento = async (input: Omit<PagamentoFuncionarioInput, "user_id">) => {
    if (!user) {
      toast.error("Usuário não autenticado");
      return null;
    }
    const data = await apiCreate<PagamentoFuncionario>("pagamentos_funcionarios", {
      ...normalizePagamentoFuncionario(input),
      user_id: user.id,
    } as Record<string, unknown>);
    await fetchPagamentos();
    toast.success("Pagamento de funcionário criado.");
    return data;
  };

  const updatePagamento = async (id: string, input: Partial<PagamentoFuncionarioInput>) => {
    const data = await apiUpdate<PagamentoFuncionario>("pagamentos_funcionarios", id, normalizePagamentoFuncionario(input) as Record<string, unknown>);
    await fetchPagamentos();
    toast.success("Pagamento atualizado.");
    return data;
  };

  const deletePagamento = async (id: string) => {
    await apiDelete("pagamentos_funcionarios", id);
    await fetchPagamentos();
    toast.success("Pagamento removido.");
  };

  useEffect(() => {
    fetchPagamentos();
  }, [user?.id, activeClinicaId]);

  return {
    pagamentos,
    loading,
    fetchPagamentos,
    createPagamento,
    updatePagamento,
    deletePagamento,
  };
}
